import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Plus, Hash, CalendarPlus, Check, X, Sparkles, AlertTriangle } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAdvisor } from "@/context/AdvisorContext";
import { ADMIN_EMPLOYEE_DETAIL } from "@/constants/testIds";
import { SalaryTab } from "./employee-detail/SalaryTab";
import { AnnouncementsHistoryTab } from "./employee-detail/AnnouncementsHistoryTab";
import { CheckpointTab } from "./employee-detail/CheckpointTab";
import { BajaTab } from "./employee-detail/BajaTab";
import { ReceiptsTab } from "./employee-detail/ReceiptsTab";
import { DocumentsTab } from "./employee-detail/DocumentsTab";
import { TrainingTab } from "./employee-detail/TrainingTab";
import { JornadaTab } from "./employee-detail/JornadaTab";
import { DossierEditDialog } from "./employee-detail/DossierEditDialog";
import { ActaAdministrativaWizard } from "./employee-detail/ActaAdministrativaWizard";
import { ActaAdministrativaHistory } from "./employee-detail/ActaAdministrativaHistory";
import { ManualConfirmDialog } from "@/components/ManualConfirmDialog";
import { MANUAL_CONFIRMATION } from "@/constants/testIds";
import { toast } from "sonner";

const REQUEST_STATUS_LABEL = {
  pending: "Pendiente de aprobación",
  awaiting_confirmation: "Esperando confirmación del empleado",
  approved: "Aprobada",
  rejected: "Rechazada",
  declined: "Rechazada por el empleado",
};

export default function EmployeeDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [employee, setEmployee] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [correctionRequests, setCorrectionRequests] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [vacationOpen, setVacationOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState("");
  const [incidentForm, setIncidentForm] = useState({ description: "", incident_date: "", policy_id: "" });
  const [vacationForm, setVacationForm] = useState({ start_date: "", end_date: "" });
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "policies");
  const [actaHistoryRefreshKey, setActaHistoryRefreshKey] = useState(0);
  const currentYear = new Date().getFullYear();
  const advisor = useAdvisor();

  const load = () => {
    api.get(`/employees/${id}`).then(({ data }) => setEmployee(data));
    api.get(`/employees/${id}/data-correction-requests`).then(({ data }) => setCorrectionRequests(data));
  };

  useEffect(() => {
    load();
    api.get("/policies").then(({ data }) => setPolicies(data));
  }, [id]);

  const resolveCorrectionRequest = async (requestId) => {
    try {
      await api.patch(`/employees/${id}/data-correction-requests/${requestId}`);
      toast.success("Solicitud marcada como resuelta");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleAssign = async () => {
    try {
      const { data } = await api.post(`/policies/${selectedPolicy}/distribute`, { employee_ids: [id] });
      toast.success(data.message);
      setAssignOpen(false);
      setSelectedPolicy("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleAcknowledgeManual = async (assignmentId, note) => {
    try {
      const { data } = await api.post(`/employees/${id}/policies/${assignmentId}/acknowledge-manual`, { note });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleIncident = async () => {
    try {
      const { data } = await api.post(`/employees/${id}/incidents`, incidentForm);
      toast.success(data.message);
      setIncidentOpen(false);
      setIncidentForm({ description: "", incident_date: "", policy_id: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleAssignVacation = async () => {
    try {
      const { data } = await api.post(`/employees/${id}/vacation-assignments`, vacationForm);
      toast.success(`${data.message} (${data.days_requested} días)`);
      setVacationOpen(false);
      setVacationForm({ start_date: "", end_date: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleApproveVacation = async (requestId) => {
    try {
      const { data } = await api.post(`/vacation-requests/${requestId}/approve`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleRejectVacation = async (requestId) => {
    try {
      const { data } = await api.post(`/vacation-requests/${requestId}/reject`, {});
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!employee) return <AdminLayout><p className="text-charcoal/60">Cargando...</p></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight">{employee.full_name}</h1>
            <p className="text-charcoal/60 mt-1">{employee.position} · {employee.work_center} · Contratado {employee.hire_date}{employee.status === "baja" && <span className="ml-2 text-red-600 font-medium">· Baja el {employee.termination_date}</span>}</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <DossierEditDialog employeeId={id} employee={employee} onUpdated={load} />
            <Button
              data-testid={ADMIN_EMPLOYEE_DETAIL.askAdvisorButton}
              variant="outline"
              className="rounded-full border-terracotta/40 text-terracotta hover:bg-terracotta/5"
              onClick={() => advisor.openAdvisorForEmployee(id, employee.full_name)}
            >
              <Sparkles className="w-4 h-4 mr-2" /> Consultar con el Asesor de RH
            </Button>
          </div>
        </div>

        {correctionRequests.filter((r) => r.status === "pending").length > 0 && (
          <div data-testid={ADMIN_EMPLOYEE_DETAIL.correctionRequestsBanner} className="mt-4 space-y-2">
            {correctionRequests.filter((r) => r.status === "pending").map((r) => (
              <div key={r.id} className="bg-amber-50 border border-amber-300 rounded-xl p-3 flex justify-between items-start gap-3">
                <p className="text-sm text-amber-800">
                  <b>Solicitud de corrección de datos:</b> {r.message}
                </p>
                <Button
                  data-testid={`${ADMIN_EMPLOYEE_DETAIL.resolveCorrectionRequestButton}-${r.id}`}
                  size="sm" variant="outline"
                  onClick={() => resolveCorrectionRequest(r.id)}
                  className="rounded-full border-amber-400 text-amber-800 h-7 text-xs shrink-0"
                >
                  Marcar resuelta
                </Button>
              </div>
            ))}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="bg-card flex-wrap h-auto">
            <TabsTrigger data-testid={ADMIN_EMPLOYEE_DETAIL.tabPolicies} value="policies">Políticas</TabsTrigger>
            <TabsTrigger data-testid={ADMIN_EMPLOYEE_DETAIL.tabDocuments} value="documents">Documentos</TabsTrigger>
            <TabsTrigger data-testid={ADMIN_EMPLOYEE_DETAIL.tabIncidents} value="incidents">Incidencias</TabsTrigger>
            <TabsTrigger data-testid={ADMIN_EMPLOYEE_DETAIL.tabVacation} value="vacation">Vacaciones</TabsTrigger>
            <TabsTrigger data-testid={ADMIN_EMPLOYEE_DETAIL.tabSalary} value="salary">Salario</TabsTrigger>
            <TabsTrigger data-testid={ADMIN_EMPLOYEE_DETAIL.tabReceipts} value="receipts">Recibos</TabsTrigger>
            <TabsTrigger data-testid={ADMIN_EMPLOYEE_DETAIL.tabAnnouncements} value="announcements">Avisos</TabsTrigger>
            <TabsTrigger data-testid={ADMIN_EMPLOYEE_DETAIL.tabTraining} value="training">Capacitación</TabsTrigger>
            <TabsTrigger data-testid="employee-detail-tab-jornada" value="jornada">Jornada</TabsTrigger>
            <TabsTrigger data-testid={ADMIN_EMPLOYEE_DETAIL.tabCheckpoint} value="checkpoint" className="flex items-center gap-1.5">
              Considera Antes de Decidir
              {!employee.current_salary && (
                <span title="Requiere registrar el salario primero (pestaña Salario)"><AlertTriangle className="w-3 h-3 text-amber-500" /></span>
              )}
            </TabsTrigger>
            <TabsTrigger data-testid={ADMIN_EMPLOYEE_DETAIL.tabBaja} value="baja">Baja</TabsTrigger>
          </TabsList>

          <TabsContent value="policies" className="space-y-3 mt-4">
            <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
              <DialogTrigger asChild>
                <Button data-testid={ADMIN_EMPLOYEE_DETAIL.assignPolicyButton} size="sm" className="bg-terracotta hover:bg-terracotta/90 rounded-full"><Plus className="w-4 h-4 mr-1" /> Asignar política</Button>
              </DialogTrigger>
              <DialogContent className="bg-bone">
                <DialogHeader><DialogTitle>Asignar política</DialogTitle></DialogHeader>
                <select data-testid={ADMIN_EMPLOYEE_DETAIL.policySelect} value={selectedPolicy} onChange={(e) => setSelectedPolicy(e.target.value)} className="w-full border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm">
                  <option value="">Selecciona una política</option>
                  {policies.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
                <Button data-testid={ADMIN_EMPLOYEE_DETAIL.confirmAssignButton} disabled={!selectedPolicy} onClick={handleAssign} className="w-full bg-petroleum rounded-full">Asignar</Button>
              </DialogContent>
            </Dialog>

            {employee.policies.map((p) => (
              <div key={p.assignment_id} className="bg-card border border-petroleum/10 rounded-xl p-4 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-medium text-charcoal">{p.title}</p>
                  {p.article_ref && <p className="text-xs font-mono-legal text-petroleum/70 mt-1">{p.article_ref}</p>}
                  {p.status === "acknowledged" && p.manual && (
                    <p data-testid={MANUAL_CONFIRMATION.policyBadge} className="text-xs text-charcoal/50 mt-1">
                      Confirmada manualmente por {p.manual_by}{p.manual_note ? `: "${p.manual_note}"` : ""}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {p.status === "acknowledged" ? (
                    <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full"><CheckCircle2 className="w-3 h-3" /> Confirmada</span>
                  ) : (
                    <>
                      <span className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full"><Clock className="w-3 h-3" /> Pendiente de acuse</span>
                      <ManualConfirmDialog
                        triggerTestId={`${MANUAL_CONFIRMATION.policyButton}-${p.assignment_id}`}
                        triggerLabel="Marcar como confirmado manualmente"
                        title="Confirmar acuse manualmente"
                        description="Para empleados sin acceso al portal (ej. firmó en papel y el documento está en su expediente físico). No se adjunta foto ni archivo — solo queda registrado quién lo marcó, cuándo, y una nota opcional."
                        onConfirm={(note) => handleAcknowledgeManual(p.assignment_id, note)}
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
            {employee.policies.length === 0 && <p className="text-charcoal/50 text-sm">Sin políticas asignadas.</p>}
          </TabsContent>

          <TabsContent value="documents">
            <DocumentsTab employeeId={id} />
          </TabsContent>

          <TabsContent value="incidents" className="space-y-3 mt-4">
            <div className="flex gap-2 flex-wrap">
              <Dialog open={incidentOpen} onOpenChange={setIncidentOpen}>
                <DialogTrigger asChild>
                  <Button data-testid={ADMIN_EMPLOYEE_DETAIL.newIncidentButton} size="sm" className="bg-terracotta hover:bg-terracotta/90 rounded-full"><Plus className="w-4 h-4 mr-1" /> Registro rápido</Button>
                </DialogTrigger>
                <DialogContent className="bg-bone">
                  <DialogHeader><DialogTitle>Registrar incidencia</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Descripción</Label>
                      <Input data-testid={ADMIN_EMPLOYEE_DETAIL.incidentDescriptionInput} value={incidentForm.description} onChange={(e) => setIncidentForm((f) => ({ ...f, description: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Fecha</Label>
                      <Input data-testid={ADMIN_EMPLOYEE_DETAIL.incidentDateInput} type="date" value={incidentForm.incident_date} onChange={(e) => setIncidentForm((f) => ({ ...f, incident_date: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Política aplicable (opcional)</Label>
                      <select value={incidentForm.policy_id} onChange={(e) => setIncidentForm((f) => ({ ...f, policy_id: e.target.value }))} className="w-full border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm">
                        <option value="">Ninguna</option>
                        {policies.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                      </select>
                    </div>
                    <Button data-testid={ADMIN_EMPLOYEE_DETAIL.incidentSubmitButton} disabled={!incidentForm.description || !incidentForm.incident_date} onClick={handleIncident} className="w-full bg-petroleum rounded-full">Guardar</Button>
                  </div>
                </DialogContent>
              </Dialog>

              <ActaAdministrativaWizard employee={employee} incidents={employee.incidents} onGenerated={() => setActaHistoryRefreshKey((k) => k + 1)} />
            </div>

            {employee.incidents.map((inc) => (
              <div key={inc.id} className="bg-card border border-petroleum/10 rounded-xl p-4">
                <div className="flex justify-between">
                  <p className="text-charcoal">{inc.description}</p>
                  <span className="text-xs text-charcoal/50 whitespace-nowrap ml-3">{inc.incident_date}</span>
                </div>
                <p className="text-xs text-charcoal/40 mt-2 flex items-center gap-1 font-mono-legal"><Hash className="w-3 h-3" /> #{inc.sequence} · {inc.hash ? `${inc.hash.slice(0, 12)}...` : "sin hash"} · capturó {inc.captured_by}</p>
              </div>
            ))}
            {employee.incidents.length === 0 && <p className="text-charcoal/50 text-sm">Sin incidencias registradas.</p>}

            <div className="pt-2 border-t border-petroleum/10">
              <ActaAdministrativaHistory employeeId={id} refreshKey={actaHistoryRefreshKey} />
            </div>
          </TabsContent>

          <TabsContent value="vacation" className="mt-4 space-y-3">
            {employee.vacation_periods.map((v) => (
              <div key={v.year} className="bg-card border border-petroleum/10 rounded-xl p-4 flex justify-between items-center">
                <p className="font-medium text-charcoal">{v.year}</p>
                <div className="text-right">
                  <p className="text-sm text-charcoal/70">{v.days_available} de {v.days_earned} días disponibles</p>
                  {v.year < currentYear && v.days_available > 0 && (
                    <p className="text-xs text-amber-700 mt-0.5">Acumulados de un año anterior, sin programar (Art. 76/78 LFT)</p>
                  )}
                </div>
              </div>
            ))}
            {employee.vacation_periods.length === 0 && <p className="text-charcoal/50 text-sm">Sin periodos registrados.</p>}

            <Dialog open={vacationOpen} onOpenChange={setVacationOpen}>
              <DialogTrigger asChild>
                <Button data-testid="assign-vacation-button" size="sm" className="bg-terracotta hover:bg-terracotta/90 rounded-full"><CalendarPlus className="w-4 h-4 mr-1" /> Asignar vacaciones</Button>
              </DialogTrigger>
              <DialogContent className="bg-bone">
                <DialogHeader><DialogTitle>Asignar vacaciones a {employee.full_name}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Fecha de inicio</Label>
                    <Input data-testid="assign-vacation-start-date" type="date" value={vacationForm.start_date} onChange={(e) => setVacationForm((f) => ({ ...f, start_date: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Fecha de fin</Label>
                    <Input data-testid="assign-vacation-end-date" type="date" value={vacationForm.end_date} onChange={(e) => setVacationForm((f) => ({ ...f, end_date: e.target.value }))} />
                  </div>
                  <p className="text-xs text-charcoal/50">Los días se calculan automáticamente según los días laborables configurados de la empresa. Quedará sujeto a la confirmación del empleado.</p>
                  <Button data-testid="assign-vacation-submit-button" disabled={!vacationForm.start_date || !vacationForm.end_date} onClick={handleAssignVacation} className="w-full bg-petroleum rounded-full">Asignar</Button>
                </div>
              </DialogContent>
            </Dialog>

            <p className="text-sm font-semibold text-charcoal pt-2">Solicitudes de vacaciones</p>
            {(employee.vacation_requests || []).map((r) => (
              <div key={r.id} data-testid="employee-detail-vacation-request-row" className="bg-card border border-petroleum/10 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-charcoal">{r.start_date} → {r.end_date} ({r.days_requested} días)</p>
                  <p className="text-xs text-charcoal/50 mt-1">{r.requested_by === "admin" ? "Asignada por admin" : "Solicitada por el empleado"} · {REQUEST_STATUS_LABEL[r.status] || r.status}</p>
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <Button data-testid={`approve-vacation-${r.id}`} size="sm" onClick={() => handleApproveVacation(r.id)} className="bg-emerald-600 hover:bg-emerald-700 rounded-full"><Check className="w-3.5 h-3.5" /></Button>
                    <Button data-testid={`reject-vacation-${r.id}`} size="sm" onClick={() => handleRejectVacation(r.id)} className="bg-red-500 hover:bg-red-600 rounded-full"><X className="w-3.5 h-3.5" /></Button>
                  </div>
                )}
              </div>
            ))}
            {(employee.vacation_requests || []).length === 0 && <p className="text-charcoal/50 text-sm">Sin solicitudes de vacaciones.</p>}
          </TabsContent>

          <TabsContent value="salary">
            <SalaryTab employeeId={id} currentSalary={employee.current_salary} history={employee.compensation_history} visibility={employee.compensation_visibility} onUpdated={load} />
          </TabsContent>

          <TabsContent value="receipts">
            <ReceiptsTab employeeId={id} />
          </TabsContent>

          <TabsContent value="announcements">
            <AnnouncementsHistoryTab employeeId={id} />
          </TabsContent>

          <TabsContent value="training">
            <TrainingTab employeeId={id} />
          </TabsContent>

          <TabsContent value="jornada">
            <JornadaTab employeeId={id} />
          </TabsContent>

          <TabsContent value="checkpoint">
            <CheckpointTab employeeId={id} onGoToSalary={() => setActiveTab("salary")} />
          </TabsContent>

          <TabsContent value="baja">
            <BajaTab employee={employee} onUpdated={load} />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
